import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export function TextCard({ title, text, light = false, testId }) {
  return (
    <div
      className={`card card-hover h-full ${light ? "border-white/15 bg-navy-light/40" : ""}`}
      data-testid={testId}
    >
      <h3 className={`font-sub text-base font-semibold ${light ? "text-white" : "text-navy"}`}>{title}</h3>
      <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
      <p className={`mt-3 text-sm leading-relaxed ${light ? "text-platinum" : "text-slate"}`}>{text}</p>
    </div>
  );
}

export function LinkCard({ title, text, to, linkLabel = "Explore", light = false, badge, testId }) {
  return (
    <div className={`card card-hover flex h-full flex-col ${light ? "border-white/15 bg-navy-light/40" : ""}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <h3 className={`font-sub text-base font-semibold ${light ? "text-white" : "text-navy"}`}>{title}</h3>
        {badge && (
          <span className="shrink-0 border border-gold/50 px-2 py-0.5 font-sub text-[0.65rem] font-semibold uppercase tracking-wider text-gold-deep">
            {badge}
          </span>
        )}
      </div>
      <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
      <p className={`mt-3 flex-1 text-sm leading-relaxed ${light ? "text-platinum" : "text-slate"}`}>{text}</p>
      <Link
        to={to}
        className={`mt-5 inline-flex min-h-[44px] items-center gap-2 font-sub text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
          light ? "text-gold hover:text-gold-soft" : "text-navy hover:text-gold-deep"
        }`}
        data-testid={testId ? `${testId}-link` : undefined}
      >
        {linkLabel}
        <ArrowRight size={15} aria-hidden="true" />
      </Link>
    </div>
  );
}

export function ProgrammeCard({ programme, testId }) {
  const p = programme;
  return (
    <article className="card card-hover flex h-full flex-col" data-testid={testId || `programme-card-${p.slug}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="bg-navy px-2.5 py-1 font-sub text-[0.65rem] font-semibold uppercase tracking-wider text-white">
          {p.category}
        </span>
        <span className="border border-platinum px-2.5 py-1 font-sub text-[0.65rem] font-semibold uppercase tracking-wider text-slate">
          {p.duration}
        </span>
      </div>
      <h3 className="mt-4 font-sub text-base font-semibold leading-snug text-navy">{p.title}</h3>
      <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate">{p.summary}</p>
      <dl className="mt-4 space-y-1 border-t border-platinum pt-4 text-xs text-slate">
        <div className="flex gap-2"><dt className="font-sub font-semibold uppercase tracking-wider text-navy">Capability</dt><dd>{p.capability}</dd></div>
        <div className="flex gap-2"><dt className="font-sub font-semibold uppercase tracking-wider text-navy">Audience</dt><dd>{p.audience}</dd></div>
        <div className="flex gap-2"><dt className="font-sub font-semibold uppercase tracking-wider text-navy">Format</dt><dd>{p.format}</dd></div>
      </dl>
      <Link to={`/programmes#${p.slug}`} className="text-link mt-5 inline-flex min-h-[44px] items-center" data-testid={`${testId || `programme-card-${p.slug}`}-view`}>
        View Programme
      </Link>
    </article>
  );
}

export function FrameworkCard({ framework, testId }) {
  const f = framework;
  return (
    <div className="card card-hover flex h-full flex-col" data-testid={testId || `framework-card-${f.slug}`}>
      <h3 className="font-sub text-base font-semibold text-navy">{f.name}</h3>
      <p className="mt-1.5 font-body text-sm italic text-gold-deep">{f.tagline}</p>
      {f.detailed ? (
        <Link
          to={`/thinking-toolbox/${f.slug}`}
          className="text-link mt-4 inline-flex min-h-[44px] items-center"
          data-testid={`${testId || `framework-card-${f.slug}`}-link`}
        >
          Explore Framework
        </Link>
      ) : (
        <p className="mt-4 border-l-2 border-gold pl-3 text-xs leading-relaxed text-slate">
          Full framework guide in preparation — detailed proprietary material will be published here.
        </p>
      )}
    </div>
  );
}
